import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  IoAddOutline,
  IoTrashOutline,
  IoPencilOutline,
  IoShieldCheckmark,
  IoClose,
  IoSaveOutline,
  IoEyeOutline,
  IoPeopleOutline,
  IoSearchOutline,
} from 'react-icons/io5';

import {
  fetchRoles,
  createRole,
  updateRole,
  deleteRole,
  getUsersByRole,
  fetchAllUsers,
  assignRoleToUser,
  revokeRoleFromUser,
} from '../../services/api';
import { PERMISSIONS_TREE } from '../../constants/permissions';

const RoleModal = ({
  isOpen,
  onClose,
  roleToEdit,
  mode,
  onSave,
  userIdToken,
}) => {
  const isViewMode = mode === 'view';
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [activeTab, setActiveTab] = useState('permissions'); // 'permissions' or 'users'
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // For assigning new users
  const [allUsersSearch, setAllUsersSearch] = useState('');
  const [queriedUsers, setQueriedUsers] = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);

  // Original save state
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('permissions');
      if (roleToEdit) {
        setName(roleToEdit.name || '');
        setDescription(roleToEdit.description || '');
        setSelectedPermissions(new Set(roleToEdit.permissions || []));
        loadAssignedUsers(roleToEdit.id || roleToEdit._id);
      } else {
        setName('');
        setDescription('');
        setSelectedPermissions(new Set());
        setAssignedUsers([]);
      }
      // Reset search when modal opens
      setAllUsersSearch('');
      setQueriedUsers([]);
    }
    // eslint-disable-next-line
  }, [isOpen, roleToEdit]);

  const loadAssignedUsers = async (roleId) => {
    setIsLoadingUsers(true);
    try {
      const res = await getUsersByRole(userIdToken, roleId);
      setAssignedUsers(res.data || []);
    } catch (error) {
      toast.error(`Failed to load assigned users: ${error.message}`);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const loadQueriedUsers = async (search = '') => {
    setIsSearchingUsers(true);
    try {
      const res = await fetchAllUsers(userIdToken, { search, limit: 50 });
      setQueriedUsers(res.users || []);
    } catch (error) {
      toast.error(`Failed to search users: ${error.message}`);
    } finally {
      setIsSearchingUsers(false);
    }
  };

  // Load queried users when switching to the users tab or searching
  useEffect(() => {
    if (activeTab === 'users' && roleToEdit) {
      const delayDebounceFn = setTimeout(() => {
        loadQueriedUsers(allUsersSearch);
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    }
    // eslint-disable-next-line
  }, [allUsersSearch, activeTab, roleToEdit]);

  const displayUsers = useMemo(() => {
    const assignedIds = new Set(assignedUsers.map((u) => u.user_id));

    if (!allUsersSearch) {
      // Show assigned users first, then unassigned
      const formattedAssigned = assignedUsers.map((a) => ({
        id: a.user_id,
        name: a.name || 'Unknown',
        email: a.email || 'Unknown',
      }));
      const unassignedFromQuery = queriedUsers.filter(
        (u) => !assignedIds.has(u.id),
      );
      return [...formattedAssigned, ...unassignedFromQuery];
    } else {
      return queriedUsers;
    }
  }, [allUsersSearch, assignedUsers, queriedUsers]);

  const handleAssignUser = async (userId) => {
    if (!roleToEdit) return;
    try {
      await assignRoleToUser(
        userIdToken,
        userId,
        roleToEdit.id || roleToEdit._id,
      );
      toast.success('User assigned successfully');
      loadAssignedUsers(roleToEdit.id || roleToEdit._id);
      // No need to reload available since displayUsers updates dynamically
    } catch (error) {
      toast.error(`Failed to assign user: ${error.message}`);
    }
  };

  const handleRevokeUser = async (userId) => {
    if (!roleToEdit) return;
    if (
      window.confirm('Are you sure you want to remove this user from the role?')
    ) {
      try {
        await revokeRoleFromUser(
          userIdToken,
          userId,
          roleToEdit.id || roleToEdit._id,
        );
        toast.success('User removed from role');
        loadAssignedUsers(roleToEdit.id || roleToEdit._id);
      } catch (error) {
        toast.error(`Failed to remove user: ${error.message}`);
      }
    }
  };

  if (!isOpen) return null;

  const handleTogglePermission = (reqPermId) => {
    if (isViewMode) return;
    const newSet = new Set(selectedPermissions);
    if (newSet.has(reqPermId)) {
      newSet.delete(reqPermId);
    } else {
      newSet.add(reqPermId);
    }
    setSelectedPermissions(newSet);
  };

  const handleToggleCategory = (categoryPermissions) => {
    if (isViewMode) return;
    const newSet = new Set(selectedPermissions);
    const allSelected = categoryPermissions.every((p) =>
      selectedPermissions.has(p.id),
    );

    categoryPermissions.forEach((p) => {
      if (allSelected) {
        newSet.delete(p.id);
      } else {
        newSet.add(p.id);
      }
    });
    setSelectedPermissions(newSet);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Role name is required');
      return;
    }

    setIsSaving(true);
    try {
      const roleData = {
        name: name.trim(),
        description: description.trim(),
        permissions: Array.from(selectedPermissions),
      };

      if (roleToEdit) {
        await updateRole(
          userIdToken,
          roleToEdit.id || roleToEdit._id,
          roleData,
        );
        toast.success('Role updated successfully');
      } else {
        await createRole(userIdToken, roleData);
        toast.success('Role created successfully');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(`Error saving role: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
      <div className="bg-1 border-line animate-fade-in-up flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl border shadow-2xl">
        {/* Modal Header */}
        <div className="border-line bg-2/30 flex items-center justify-between rounded-t-xl border-b p-5">
          <h3 className="text-p-text flex items-center gap-2 text-xl font-bold">
            <IoShieldCheckmark className="text-[#5652CF]" />
            {isViewMode
              ? 'View Role'
              : roleToEdit
                ? 'Edit Role'
                : 'Create New Role'}
          </h3>
          <button
            onClick={onClose}
            className="hover:bg-2 rounded-full p-1 transition-colors"
          >
            <IoClose className="text-s-text h-6 w-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="custom-scrollbar flex-1 space-y-6 overflow-y-auto p-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label
                htmlFor="role-name"
                className="text-p-text mb-2 block text-sm font-semibold uppercase tracking-wider"
              >
                Role Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isViewMode}
                placeholder="e.g. Content Manager"
                className="bg-2 border-line text-p-text disabled:bg-1 w-full rounded-lg border px-4 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-[#5652CF] disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>
            <div>
              <label
                htmlFor="role-description"
                className="text-p-text mb-2 block text-sm font-semibold uppercase tracking-wider"
              >
                Description
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isViewMode}
                placeholder="Brief summary of this role..."
                className="bg-2 border-line text-p-text disabled:bg-1 w-full rounded-lg border px-4 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-[#5652CF] disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>
          </div>

          {/* Tabs */}
          {roleToEdit && (
            <div className="border-line mb-6 flex border-b">
              <button
                className={`border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'permissions' ? 'border-[#5652CF] text-[#5652CF]' : 'text-s-text hover:text-p-text border-transparent'}`}
                onClick={() => setActiveTab('permissions')}
              >
                <span className="flex items-center gap-2">
                  <IoShieldCheckmark /> Permissions
                </span>
              </button>
              <button
                className={`border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'users' ? 'border-[#5652CF] text-[#5652CF]' : 'text-s-text hover:text-p-text border-transparent'}`}
                onClick={() => setActiveTab('users')}
              >
                <span className="flex items-center gap-2">
                  <IoPeopleOutline /> Assigned Users
                  <span className="bg-2/80 rounded-full px-2 py-0.5 text-xs">
                    {assignedUsers.length}
                  </span>
                </span>
              </button>
            </div>
          )}

          {/* Permissions Tab Content */}
          <div className={activeTab === 'permissions' ? 'block' : 'hidden'}>
            <div className="mb-4 flex items-center justify-between">
              <label
                htmlFor="permissions"
                className="text-p-text block text-sm font-semibold uppercase tracking-wider"
              >
                Permissions
              </label>
              <span className="rounded-md bg-[#5652CF]/10 px-2 py-1 text-xs font-medium text-blue-400">
                {selectedPermissions.size} Selected
              </span>
            </div>

            <div className="space-y-4">
              {PERMISSIONS_TREE.map((category, idx) => {
                const categoryPermissions = category.permissions;
                const isAllSelected =
                  categoryPermissions.length > 0 &&
                  categoryPermissions.every((p) =>
                    selectedPermissions.has(p.id),
                  );
                const isSomeSelected =
                  categoryPermissions.some((p) =>
                    selectedPermissions.has(p.id),
                  ) && !isAllSelected;

                return (
                  <div
                    key={idx}
                    className="bg-2/40 border-line hover:bg-2/50 overflow-hidden rounded-lg border transition-all"
                  >
                    {/* eslint-disable-next-line */}
                    <div
                      className={`bg-2/80 border-line flex items-center justify-between border-b px-4 py-3 transition-colors ${isViewMode ? 'cursor-default' : 'hover:bg-2 cursor-pointer'}`}
                      onClick={() => handleToggleCategory(categoryPermissions)}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          readOnly={isViewMode}
                          ref={(input) => {
                            if (input) input.indeterminate = isSomeSelected;
                          }}
                          onClick={(e) => {
                            if (isViewMode) e.preventDefault();
                          }}
                          onChange={() => {}} // Handled by div onClick
                          className={`bg-1 border-line h-4 w-4 rounded text-[#5652CF] focus:ring-2 focus:ring-[#5652CF] ${isViewMode ? 'pointer-events-none cursor-default opacity-100' : ''}`}
                        />
                        <span className="text-p-text font-bold">
                          {category.category}
                        </span>
                      </div>
                      <span className="text-s-text text-xs font-medium">
                        {
                          categoryPermissions.filter((p) =>
                            selectedPermissions.has(p.id),
                          ).length
                        }{' '}
                        / {categoryPermissions.length}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
                      {categoryPermissions.map((perm) => (
                        <div
                          key={perm.id}
                          className={`flex items-start gap-3 rounded-lg border border-transparent p-3 transition-colors ${isViewMode ? 'cursor-default' : 'hover:bg-1 hover:border-line group cursor-pointer'}`}
                        >
                          <div className="pt-0.5">
                            <input
                              type="checkbox"
                              checked={selectedPermissions.has(perm.id)}
                              readOnly={isViewMode}
                              onClick={(e) => {
                                if (isViewMode) e.preventDefault();
                              }}
                              onChange={() => handleTogglePermission(perm.id)}
                              className={`bg-1 border-line h-4 w-4 rounded text-[#5652CF] focus:ring-2 focus:ring-[#5652CF] ${isViewMode ? 'pointer-events-none cursor-default opacity-100' : ''}`}
                            />
                          </div>
                          <div>
                            <p className="text-p-text text-sm font-semibold transition-colors group-hover:text-blue-400">
                              {perm.label}
                            </p>
                            <p className="text-s-text mt-0.5 text-xs">
                              {perm.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Users Tab Content */}
          {roleToEdit && (
            <div
              className={activeTab === 'users' ? 'block space-y-4' : 'hidden'}
            >
              <div className="bg-2/30 border-line rounded-xl border p-4">
                <h4 className="text-p-text mb-3 text-sm font-bold">
                  Manage Users for {roleToEdit.name}
                </h4>
                <div className="bg-1 border-line text-p-text mb-4 flex items-center rounded-lg border px-3 py-2 transition-all focus-within:ring-2 focus-within:ring-[#5652CF]">
                  <IoSearchOutline className="text-s-text mr-2 text-lg" />
                  <input
                    type="text"
                    className="w-full bg-transparent text-sm focus:outline-none"
                    placeholder="Search users by name or email to assign..."
                    value={allUsersSearch}
                    onChange={(e) => setAllUsersSearch(e.target.value)}
                  />
                  {isSearchingUsers && (
                    <div className="ml-2 h-4 w-4 animate-spin rounded-full border-2 border-[#5652CF] border-t-transparent"></div>
                  )}
                </div>

                <div className="border-line bg-1 overflow-hidden rounded-xl border">
                  <ul className="divide-line custom-scrollbar max-h-96 divide-y overflow-y-auto">
                    {isLoadingUsers || isSearchingUsers ? (
                      <li className="text-s-text p-4 text-center text-sm">
                        <div className="mr-2 inline-block h-5 w-5 animate-spin rounded-full border-2 border-[#5652CF] border-t-transparent"></div>
                        Loading users...
                      </li>
                    ) : displayUsers.length === 0 ? (
                      <li className="text-s-text p-4 text-center text-sm">
                        No users found.
                      </li>
                    ) : (
                      displayUsers.map((user) => {
                        const isAssigned = assignedUsers.some(
                          (a) => a.user_id === user.id,
                        );
                        return (
                          <li
                            key={user.id}
                            className="hover:bg-2/50 flex items-center justify-between p-3 transition-colors"
                          >
                            <div className="flex w-full cursor-pointer items-center gap-3">
                              <input
                                type="checkbox"
                                checked={isAssigned}
                                onChange={(e) => {
                                  if (e.target.checked)
                                    handleAssignUser(user.id);
                                  else handleRevokeUser(user.id);
                                }}
                                className={`bg-1 border-line h-4 w-4 rounded text-[#5652CF] focus:ring-2 focus:ring-[#5652CF]`}
                              />
                              <div>
                                <p className="text-p-text text-sm font-semibold">
                                  {user.name}
                                </p>
                                <p className="text-s-text text-xs">
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="border-line bg-2/30 flex flex-col-reverse items-center justify-between gap-3 rounded-b-xl border-t p-5 sm:flex-row">
          <p className="text-s-text text-center text-xs sm:text-left">
            {roleToEdit &&
              roleToEdit.is_system_role &&
              !isViewMode &&
              'Note: System roles cannot be completely deleted.'}
          </p>
          <div className="flex w-full gap-3 sm:w-auto">
            <button
              onClick={onClose}
              className={`flex-1 rounded-lg px-5 py-2.5 text-sm font-semibold transition-all sm:flex-none ${isViewMode ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20 hover:bg-[#5652CF]' : 'text-s-text border-line hover:bg-2 hover:text-p-text border bg-transparent'}`}
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition-all hover:-translate-y-0.5 hover:bg-[#5652CF] disabled:cursor-wait disabled:opacity-50 sm:flex-none"
              >
                <IoSaveOutline className="text-lg" />
                {isSaving ? 'Saving...' : 'Save Role'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const RolesPermissionsPanel = ({ userIdToken }) => {
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const loadRoles = async () => {
    setIsLoading(true);
    try {
      const res = await fetchRoles(userIdToken);
      setRoles(res.data || []);
    } catch (error) {
      toast.error(`Failed to load roles: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userIdToken) {
      loadRoles();
    }
    // eslint-disable-next-line
  }, [userIdToken]);

  const handleCreateNew = () => {
    setRoleToEdit(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEdit = (role, e) => {
    if (e) e.stopPropagation();
    setRoleToEdit(role);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleView = (role) => {
    setRoleToEdit(role);
    setModalMode('view');
    setIsModalOpen(true);
  };

  const handleDelete = async (role, e) => {
    if (e) e.stopPropagation();
    if (role.is_system_role) {
      toast.error('Cannot delete system roles.');
      return;
    }

    if (
      window.confirm(`Are you sure you want to delete the role "${role.name}"?`)
    ) {
      try {
        await deleteRole(userIdToken, role.id || role._id);
        toast.success('Role deleted successfully');
        loadRoles();
      } catch (error) {
        toast.error(`Failed to delete role: ${error.message}`);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-1 border-line flex flex-col justify-between gap-4 rounded-xl border p-5 shadow-lg sm:flex-row sm:items-center">
        <div>
          <h2 className="text-p-text flex items-center gap-2 text-xl font-bold">
            <IoShieldCheckmark className="text-[#5652CF]" />
            Roles & Permissions
          </h2>
          <p className="text-s-text mt-1 text-sm">
            Manage custom roles and configure access limits.
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-[#5652CF]"
        >
          <IoAddOutline className="text-lg" /> Create Role
        </button>
      </div>

      <div className="bg-1 border-line overflow-hidden rounded-xl border shadow-lg">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-2/50 text-s-text border-line border-b text-xs uppercase">
              <tr>
                <th scope="col" className="px-6 py-4 font-bold tracking-wider">
                  Role Name
                </th>
                <th
                  scope="col"
                  className="hidden px-6 py-4 font-bold tracking-wider md:table-cell"
                >
                  Description
                </th>
                <th
                  scope="col"
                  className="w-32 px-6 py-4 text-center font-bold tracking-wider"
                >
                  Permissions
                </th>
                <th
                  scope="col"
                  className="w-32 px-6 py-4 text-center font-bold tracking-wider"
                >
                  Users
                </th>
                <th
                  scope="col"
                  className="w-32 px-6 py-4 text-center font-bold tracking-wider"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="w-24 px-6 py-4 text-right font-bold tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-line divide-y">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="text-s-text px-6 py-8 text-center">
                    <div className="flex animate-pulse flex-col items-center gap-3">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#5652CF] border-t-transparent"></div>
                      Loading roles...
                    </div>
                  </td>
                </tr>
              ) : roles.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-s-text px-6 py-8 text-center">
                    No roles found.
                  </td>
                </tr>
              ) : (
                roles.map((role) => (
                  <tr
                    key={role.id || role._id}
                    onClick={() => handleView(role)}
                    className="hover:bg-2/30 group cursor-pointer transition-colors"
                  >
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-p-text font-bold">{role.name}</div>
                    </td>
                    <td className="text-s-text hidden px-6 py-4 md:table-cell">
                      {role.description || (
                        <span className="italic opacity-50">
                          No description
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center justify-center rounded-full bg-[#5652CF]/10 px-2.5 py-1 text-xs font-bold text-blue-400">
                        {role.permissions?.length || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center justify-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-bold text-emerald-400">
                        <IoPeopleOutline className="text-sm" />
                        {role.user_count ?? 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {role.is_system_role ? (
                        <span className="inline-flex items-center rounded bg-purple-500/10 px-2 py-0.5 text-xs font-medium text-purple-400 ring-1 ring-inset ring-purple-500/20">
                          System
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded bg-gray-500/10 px-2 py-0.5 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-500/20">
                          Custom
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-100 transition-opacity group-hover:opacity-100 sm:opacity-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleView(role);
                          }}
                          className="text-s-text bg-2/50 rounded-md p-1.5 transition-colors hover:bg-[#5652CF]/10 hover:text-blue-400"
                          title="View Role"
                        >
                          <IoEyeOutline className="text-lg" />
                        </button>
                        {!role.is_system_role && (
                          <button
                            onClick={(e) => handleEdit(role, e)}
                            className="text-s-text bg-2/50 rounded-md p-1.5 transition-colors hover:bg-[#5652CF]/10 hover:text-blue-400"
                            title="Edit Role"
                          >
                            <IoPencilOutline className="text-lg" />
                          </button>
                        )}
                        {!role.is_system_role && (
                          <button
                            onClick={(e) => handleDelete(role, e)}
                            className="text-s-text bg-2/50 rounded-md p-1.5 transition-colors hover:bg-red-500/10 hover:text-red-400"
                            title="Delete Role"
                          >
                            <IoTrashOutline className="text-lg" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <RoleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        roleToEdit={roleToEdit}
        mode={modalMode}
        onSave={loadRoles}
        userIdToken={userIdToken}
      />
    </div>
  );
};
